class BackendAPI {
  constructor() {
    // ALWAYS use the working backend URL - no fallbacks to broken endpoints
    this.baseURL = process.env.REACT_APP_BACKEND_URL || 'https://sportiq-backend-api-clean.arturovillanueva1994.workers.dev/api';
    this.token = localStorage.getItem('sportiq_token');
    
    // Log which URL we're using for debugging
    console.log('🔗 Backend URL:', this.baseURL);
  }

  // Set auth token
  setToken(token) {
    this.token = token;
    localStorage.setItem('sportiq_token', token);
  }

  // Clear auth token
  clearToken() {
    this.token = null;
    localStorage.removeItem('sportiq_token');
  }

  // Generic request method
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    const defaultHeaders = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      defaultHeaders['Authorization'] = `Bearer ${this.token}`;
    }

    const config = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    try {
      console.log(`🚀 API Call: ${options.method || 'GET'} ${url}`);
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log(`✅ API Response:`, result);
      return result;
    } catch (error) {
      console.error(`❌ API Error (${endpoint}):`, error);
      throw error;
    }
  }

  // ==================== AUTH ENDPOINTS ====================

  // Register new user
  async registerUser(userData) {
    return this.request('/auth/register', { // ✅ Fixed: added /auth
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  // Login user
  async loginUser(email, password) {
    return this.request('/auth/login', { // ✅ Fixed: added /auth  
      method: 'POST',
      body: JSON.stringify({ email, password })
    });
  }

  // Update user profile
  async updateUser(userData) {
    return this.request('/user', {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
  }

  // Get user profile
  async getUser(email) {
    return this.request(`/user?email=${encodeURIComponent(email)}`);
  }

  // ==================== GAMES ENDPOINTS ====================

  // Get games for a specific week
  async getGames(week = null, year = 2025) {
    const params = new URLSearchParams();
    if (week) params.append('week', week);
    params.append('year', year);
    
    return this.request(`/games?${params}`);
  }

  // Get live scores
  async getScores(week = null, year = 2025) {
    const params = new URLSearchParams();
    if (week) params.append('week', week);
    params.append('year', year);
    
    return this.request(`/scores?${params}`);
  }

  // ==================== SCORECARD ENDPOINTS ====================

  // Submit a scorecard
  async submitScorecard(scorecardData) {
    return this.request('/scorecard', {
      method: 'POST',
      body: JSON.stringify(scorecardData)
    });
  }

  // Get user's scorecards
  async getUserScorecards(userEmail, week = null, tier = null) {
    const params = new URLSearchParams();
    params.append('email', userEmail);
    if (week) params.append('week', week);
    if (tier) params.append('tier', tier);
    
    return this.request(`/user-scorecards?${params}`);
  }

  // ==================== LEADERBOARD ENDPOINTS ====================

  // Get leaderboard with tier support
  async getLeaderboard(week = null, tier = 'all', year = 2025) {
    const params = new URLSearchParams();
    if (week) params.append('week', week);
    if (tier && tier !== 'all') params.append('tier', tier);
    params.append('year', year);
    
    return this.request(`/leaderboard?${params}`);
  }

  // Get weekly winner with tier support
  async getWeeklyWinner(week = null, tier = 'playmaker', year = 2025) {
    const params = new URLSearchParams();
    if (week) params.append('week', week);
    if (tier) params.append('tier', tier);
    params.append('year', year);
    
    return this.request(`/weekly-winner?${params}`);
  }

  // Get current leader with tier support
  async getCurrentLeader(week = null, tier = 'playmaker', year = 2025) {
    const params = new URLSearchParams();
    if (week) params.append('week', week);
    if (tier) params.append('tier', tier);
    params.append('year', year);
    
    return this.request(`/current-leader?${params}`);
  }

  // ==================== UTILITY METHODS ====================

  // Get current NFL week
  async getCurrentWeek() {
    return this.request('/current-week');
  }

  // Check if games are live
  async areGamesLive(week = null, year = 2025) {
    const params = new URLSearchParams();
    if (week) params.append('week', week);
    params.append('year', year);
    
    return this.request(`/games-live?${params}`);
  }

  // ==================== TIER CONFIGURATION ====================

  // Get tier configuration
  getTierConfig() {
    return {
      playmaker: { 
        name: 'Playmaker', 
        price: 25, 
        color: 'bg-orange-600', 
        textColor: 'text-orange-400',
        borderColor: 'border-orange-500'
      },
      pro: { 
        name: 'Pro', 
        price: 50, 
        color: 'bg-gray-400', 
        textColor: 'text-gray-300',
        borderColor: 'border-gray-400'
      },
      legend: { 
        name: 'Legend', 
        price: 100, 
        color: 'bg-yellow-500', 
        textColor: 'text-yellow-400',
        borderColor: 'border-yellow-500'
      }
    };
  }

  // Validate tier and price
  validateTierPrice(tier, price) {
    const tiers = this.getTierConfig();
    return tiers[tier] && tiers[tier].price === price;
  }

  // ==================== ERROR HANDLING HELPERS ====================

  // Check if error is authentication related
  isAuthError(error) {
    return error.message.includes('401') || 
           error.message.includes('Unauthorized') ||
           error.message.includes('Invalid token');
  }

  // Handle auth errors
  handleAuthError() {
    this.clearToken();
    // Redirect to login if in browser
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  }
}

// Create singleton instance
const backendAPI = new BackendAPI();

// Export both the class and the instance
export { BackendAPI };
export default backendAPI;
